// ═══════════════════════════════════════════════════════════════════════════════
// Display-name resolver — client side
//
// Pure functions used by the `useDisplayName` hook and the `<UserName>`
// component.  All lookups stay in-memory against the `registeredUsers`
// slice of AppState, which is populated by /api/users/directory on startup.
//
// Identity stays UPN/OID based; this module only translates an identifier
// into a display string.  Never throws.  A bare object id (UUID) is NEVER
// returned as a "name" — if resolution gets that far it returns '' so the UI
// can show a friendly fallback (and lazily resolve via Graph).  A UPN degrades
// to its local part.
// ═══════════════════════════════════════════════════════════════════════════════
import type { User } from '../types';

/** Returns true if the input string looks like a UPN/email. */
export function looksLikeUpn(s: string | undefined | null): boolean {
  return !!s && typeof s === 'string' && s.includes('@');
}

/** Returns true if the input string is a bare UUID / Entra OID (e.g.
 *  "58ec7596-f0a8-4df5-a4b1-bc3e57fae9fc").  Used to detect when display-name
 *  resolution failed and degraded all the way back to the raw object id, so
 *  the UI can show a friendly fallback instead of leaking a raw GUID. */
export function looksLikeUuid(s: string | undefined | null): boolean {
  return (
    !!s &&
    typeof s === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim())
  );
}

/** "zmoore@patriotconsulting.com" → "zmoore". Returns input unchanged when
 *  there is no `@`.  Used as a graceful fallback when no real display name
 *  is available. */
export function prettyFromUpn(input: string | undefined | null): string {
  if (!input) return '';
  if (!input.includes('@')) return input;
  return input.split('@')[0];
}

/** "Analyst 35"-style placeholder minted by the QA spreadsheet importer when a
 *  sheet column carried only a number (src/lib/qaSpreadsheet.ts). Persisted on
 *  legacy qa-scores records — never a real human name, so it must not shadow a
 *  directory match. */
function looksLikeAnalystPlaceholder(s: string): boolean {
  return /^Analyst \d+$/i.test(s.trim());
}

/** Returns true when a display name is just the local part of the user's own
 *  email (e.g. "analyst59" with email "analyst59@patriotconsulting.com").
 *  These are placeholders persisted by earlier degradation paths (profile
 *  created while Microsoft Graph was unreachable) — never a name a human
 *  chose — so they must not be trusted as real names. */
export function isLocalPartPlaceholder(
  displayName: string | undefined | null,
  email: string | undefined | null,
): boolean {
  if (!displayName || !email || !email.includes('@')) return false;
  return displayName.trim().toLowerCase() === email.split('@')[0].trim().toLowerCase();
}

/** A stored display name is trustworthy only when it's a real human name —
 *  NOT a UPN/email, NOT a bare object id, and NOT an "Analyst N" placeholder.
 *  A surprising amount of legacy data has the submitter's raw OID persisted in
 *  the *Name field (it was written before server-side Graph resolution
 *  succeeded); trusting that would short-circuit the directory lookup and
 *  surface a UUID. */
function isRealName(s: string | undefined | null): s is string {
  return !!s && !looksLikeUpn(s) && !looksLikeUuid(s) && !looksLikeAnalystPlaceholder(s);
}

export interface ResolvedDisplayName {
  name: string;
  /** True when `name` is only a degraded fallback (a UPN's local part or a
   *  persisted local-part placeholder like "analyst59") rather than a real
   *  human name — the caller may lazily re-resolve the id via Graph while
   *  showing the fallback in the meantime. */
  degraded: boolean;
}

/**
 * Resolve a display name for either an OID or a UPN/email, reporting whether
 * the result is a real name or only a degraded fallback.
 *
 * Order of precedence:
 *   1. A stored real name (a human name — not a UPN, a bare OID, or a
 *      local-part placeholder of the identity's known email).
 *   2. Directory match — by Entra OID first, then by email/UPN.
 *   3. Graceful degradation (`degraded: true`): a UPN → its local part; a
 *      bare OID/UUID → '' (so the caller can show a fallback and/or lazily
 *      resolve via Graph).
 *
 * A raw object id is NEVER returned as a name.
 */
export function resolveDisplayNameDetailed(
  idOrUpn: string | undefined | null,
  registeredUsers: User[],
  /** Optional display name already attached to the record being rendered
   *  (e.g. `post.authorName` stored at write time).  Trusted only when it is
   *  a real name — a UPN-, UUID-, or placeholder-shaped value is ignored in
   *  favour of the directory lookup so the row reflects the user's current
   *  display name. */
  storedDisplayName?: string,
): ResolvedDisplayName {
  // Directory lookup — by Entra OID first, then by email/UPN.  Located up
  // front so the entry's email can vet candidate names for the local-part
  // placeholder shape ("analyst59" for analyst59@…).
  const needle = idOrUpn?.toLowerCase();
  const byId = needle ? registeredUsers.find(u => u.id?.toLowerCase() === needle) : undefined;
  const byEmail = needle ? registeredUsers.find(u => u.email?.toLowerCase() === needle) : undefined;

  // The email this identity is known by — used to detect placeholder names.
  const knownEmail =
    byId?.email || byEmail?.email || (looksLikeUpn(idOrUpn) ? idOrUpn! : undefined);

  const trustworthy = (s: string | undefined | null): s is string =>
    isRealName(s) && !isLocalPartPlaceholder(s, knownEmail);

  // 1. Trust a stored real name outright — cheap, and still correct for users
  //    who have since left the directory.
  if (trustworthy(storedDisplayName)) return { name: storedDisplayName!, degraded: false };

  // 2. Directory match.
  if (byId && trustworthy(byId.name)) return { name: byId.name, degraded: false };
  if (byEmail && trustworthy(byEmail.name)) return { name: byEmail.name, degraded: false };

  // 3. Graceful degradation that never leaks a raw object id.
  const upnish = looksLikeUpn(storedDisplayName)
    ? storedDisplayName
    : looksLikeUpn(idOrUpn)
      ? idOrUpn
      : knownEmail ?? null;
  if (upnish) return { name: prettyFromUpn(upnish), degraded: true };
  // A bare OID/UUID (or nothing) resolves to '' — the UI shows a fallback and
  // may lazily resolve it via Graph.  Any other legacy non-UUID id is echoed
  // unchanged to preserve historical behaviour.
  if (!idOrUpn || looksLikeUuid(idOrUpn)) return { name: '', degraded: !!idOrUpn };
  return { name: idOrUpn, degraded: true };
}

/** Back-compat convenience wrapper — resolves to just the display string. */
export function resolveDisplayName(
  idOrUpn: string | undefined | null,
  registeredUsers: User[],
  storedDisplayName?: string,
): string {
  return resolveDisplayNameDetailed(idOrUpn, registeredUsers, storedDisplayName).name;
}
